import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useApi } from '../../hooks/useApi';
import TerminalIcon from '@mui/icons-material/Terminal';
import ErrorIcon from '@mui/icons-material/Error';
import LoginIcon from '@mui/icons-material/Login';

const Login = () => {
  const { login } = useAuth();
  const { auth } = useApi();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await auth.login(username, password);
      login(res.token, res.user);
      navigate('/', { replace: true });
    } catch (err) {
      setError((err as Error).message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
      {/* Logo */}
      <div className="flex items-center gap-3 mb-8">
        <div className="w-12 h-12 rounded-xl bg-sidebar-bg flex items-center justify-center shadow-lg">
          <TerminalIcon className="text-primary-fixed-dim text-[28px]" />
        </div>
        <div>
          <div className="font-headline-md text-on-surface font-bold tracking-tight">Scraper Panel</div>
          <div className="font-label-caps text-on-surface-variant/60">Admin Terminal</div>
        </div>
      </div>

      {/* Login card */}
      <div className="w-full max-w-sm bg-surface border border-border-subtle rounded-xl shadow-md overflow-hidden">
        <div className="p-6">
          <h2 className="font-headline-sm text-on-surface font-semibold mb-1">Welcome back</h2>
          <p className="font-body-sm text-on-surface-variant mb-6">Sign in to continue</p>

          {error && (
            <div className="bg-danger/10 border border-danger/20 text-danger text-body-sm rounded-lg px-3 py-2 mb-4 flex items-center gap-2">
              <ErrorIcon className="text-[16px]" />
              {error}
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="text-[10px] font-label-caps text-on-surface-variant uppercase block mb-1.5">
                Username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full bg-surface-high border border-border-subtle rounded-lg px-3 py-2.5 text-body-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                placeholder="Enter username"
                required
                autoFocus
              />
            </div>

            <div>
              <label className="text-[10px] font-label-caps text-on-surface-variant uppercase block mb-1.5">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-surface-high border border-border-subtle rounded-lg px-3 py-2.5 text-body-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                placeholder="Enter password"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary-container text-on-primary py-2.5 rounded-lg font-body-sm font-semibold hover:bg-primary transition-all active:scale-[0.98] flex items-center justify-center gap-2 shadow-md disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-on-primary border-t-transparent rounded-full animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  <LoginIcon className="text-[18px]" />
                  Sign in
                </>
              )}
            </button>
          </form>
        </div>

      </div>
    </div>
  );
};

export default Login;
